// src/utils/Header/Header.jsx
import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import "./Header.css";
import aultralogo from "../../assets/logo/aultralogo.jpg";
import aultraname from "../../assets/logo/aultraheading.png";

const Header = () => {
  const [headerMenuOpen, setHeaderMenuOpen] = useState(false);
  const [headerScrolled, setHeaderScrolled] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();

  // Handle scroll effect for header
  useEffect(() => {
    const handleScroll = () => {
      setHeaderScrolled(window.scrollY > 50);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (headerMenuOpen && !event.target.closest(".header-container") && !event.target.closest(".header-mobile-navigation")) {
        setHeaderMenuOpen(false);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [headerMenuOpen]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (headerMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [headerMenuOpen]);

  const toggleMenu = (e) => {
    e.stopPropagation();
    setHeaderMenuOpen(!headerMenuOpen);
  };

  const handleNavClick = (path) => {
    navigate(path);
    setHeaderMenuOpen(false);
  };

  // Helper function to check if a nav item is active
  const isNavItemActive = (path) => {
    // For the Products link, also match /product/:id routes
    if (path === '/products') {
      return location.pathname === '/products' || location.pathname.startsWith('/product/');
    }
    return location.pathname === path;
  };

  const navigationItems = [
    { name: "Home", path: "/", ariaLabel: "Navigate to homepage" },
    { name: "About", path: "/about", ariaLabel: "Learn about our company" },
    { name: "Products", path: "/products", ariaLabel: "View our paint products" },
    { name: "Catalogue", path: "/catalogue", ariaLabel: "Browse our product catalogue" },
    { name: "Calculator", path: "/calculator", ariaLabel: "Explore painting tools and resources" },
    { name: "Dealer Info", path: "/dealer-info", ariaLabel: "Find dealer information" },
    { name: "Contact Us", path: "/contactus", ariaLabel: "Find Company information" },
  ];

  return (
    <header
      className={`header-main ${headerScrolled ? "header-scrolled" : ""}`}
      role="banner"
      aria-label="Main site header"
    >
      <div className="header-container">
        {/* Logo and Company Name Section */}
        <div className="header-brand">
          <Link to="/" className="header-brand-link" aria-label="Go to homepage" onClick={() => setHeaderMenuOpen(false)}>
            <img
              src={aultralogo}
              alt="Paint Company Logo - Professional Paint Solutions"
              className="header-logo"
              width="50"
              height="50"
              loading="eager"
            />
            <img
              src={aultraname}
              alt="Paint Company Name"
              className="header-company-name"
              height="40"
              loading="eager"
            />
          </Link>
        </div>

        {/* Desktop Navigation */}
        <nav className="header-navigation" role="navigation" aria-label="Main navigation">
          <ul className="header-nav-list">
            {navigationItems.map((item) => (
              <li key={item.name} className="header-nav-item">
                <Link
                  to={item.path}
                  className={`header-nav-link ${
                    isNavItemActive(item.path) ? "header-nav-link-active" : ""
                  }`}
                  aria-label={item.ariaLabel}
                  aria-current={isNavItemActive(item.path) ? "page" : undefined}
                >
                  {item.name}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        {/* Mobile Menu Toggle */}
        <button
          className={`header-menu-toggle ${headerMenuOpen ? "header-menu-toggle-open" : ""}`}
          onClick={toggleMenu}
          aria-expanded={headerMenuOpen}
          aria-controls="header-mobile-menu"
          aria-label={headerMenuOpen ? "Close navigation menu" : "Open navigation menu"}
          type="button"
        >
          <span className="header-menu-toggle-line"></span>
          <span className="header-menu-toggle-line"></span>
          <span className="header-menu-toggle-line"></span>
        </button>

        {/* Mobile Navigation */}
        <nav
          id="header-mobile-menu"
          className={`header-mobile-navigation ${headerMenuOpen ? "header-mobile-navigation-open" : ""}`}
          role="navigation"
          aria-label="Mobile navigation"
          aria-hidden={!headerMenuOpen}
        >
          <ul className="header-mobile-nav-list">
            {navigationItems.map((item) => (
              <li key={item.name} className="header-mobile-nav-item">
                <button
                  onClick={() => handleNavClick(item.path)}
                  className={`header-mobile-nav-link ${
                    isNavItemActive(item.path) ? "header-mobile-nav-link-active" : ""
                  }`}
                  aria-label={item.ariaLabel}
                  aria-current={isNavItemActive(item.path) ? "page" : undefined}
                  type="button"
                >
                  {item.name}
                </button>
              </li>
            ))}
          </ul>
        </nav>

        {/* Mobile Menu Overlay */}
        {headerMenuOpen && (
          <div
            className="header-mobile-overlay"
            onClick={() => setHeaderMenuOpen(false)}
            aria-hidden="true"
          ></div>
        )}
      </div>
    </header>
  );
};

export default Header;